import { useState, useEffect, useRef, useCallback } from "react";
import { normalizarEtapaVendasLegada } from "../../../utils/vendasFunnel.js";

export const useUsers = (
  externalFilter,
  _externalSetFilter,
  externalOrderFilter,
  externalMonthFilter = "all"
) => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [paidCount, setPaidCount] = useState(0);
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [testCount, setTestCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const observerRef = useRef();
  const [orderFilter, setOrderFilter] = useState(externalOrderFilter || "DESC"); // <-- NOVO

  const fetchUsers = useCallback(
    async (page, search = searchQuery) => {
      setLoading(true);

      try {
        let url = `${process.env.REACT_APP_API_BASE_URL}/users?page=${page}&limit=10&sort=created_at&order=${externalOrderFilter}`;

        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        if (externalFilter !== "all") {
          url += `&status=${externalFilter}`;
        }
        if (externalMonthFilter && externalMonthFilter !== "all") {
          url += `&month=${encodeURIComponent(externalMonthFilter)}`;
        }

        const response = await fetch(url);
        const data = await response.json();

        if (response.ok) {
          setUsers((prevUsers) => {
            let newUsers;
            if (page <= 1) {
              newUsers = data.data;
            } else {
              newUsers = [...prevUsers, ...data.data];
            }
            return newUsers;
          });
          setTotalUsers(data.total);
          setPaidCount(data.paidCount);
          setUnpaidCount(data.unpaidCount);
          setTestCount(data.testCount || 0);
          setHasMore(page < data.totalPages);
          setCurrentPage(page);
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, externalFilter, externalOrderFilter, externalMonthFilter]
  );

  // Busca inicial e quando searchQuery ou filter mudam
  useEffect(() => {
    const debounceSearch = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, searchQuery);
    }, 300);
    return () => clearTimeout(debounceSearch);
  }, [searchQuery, externalFilter, externalOrderFilter, externalMonthFilter, fetchUsers]);

  // Scroll infinito
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchUsers(currentPage + 1, searchQuery); // corrigido
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [
    currentPage,
    hasMore,
    loading,
    searchQuery,
    externalFilter,
    externalMonthFilter,
    orderFilter,
    fetchUsers,
  ]);

  const handleRefresh = useCallback(() => {
    setUsers([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchUsers(1, searchQuery);
  }, [fetchUsers, searchQuery]);

  const handleDeleteUser = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/delete-user/${id}`,
        { method: "DELETE" }
      );

      if (!response.ok) throw new Error("Erro ao excluir usuário.");

      const updatedUsers = users.filter((user) => user.id !== id);
      setUsers(updatedUsers);
      setTotalUsers((prev) => prev - 1);
      fetchUsers(1, searchQuery);
    } catch (error) {
      console.error("Erro ao excluir usuário:", error);
    }
  };

  const handleUpdateDiscount = (userId, payload) => {
    if (!payload || typeof payload !== "object") return;

    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? {
              ...u,
              discount_type: payload.discount_type ?? null,
              custom_value: payload.custom_value ?? null,
              subscription_days_override:
                payload.subscription_days_override ?? null,
              discount_notified:
                payload.discount_type && payload.discount_type !== "none"
                  ? 0
                  : null,
            }
          : u
      )
    );
  };


  const handleUpdateTags = async (userId, updatedUser) => {
    try {
      const updatedUsers = users.map((user) =>
        user.id === userId ? updatedUser : user
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error("Erro ao atualizar tags:", error);
    }
  };

  const handleRenewalNotified = (userId) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, renewal_notified: 1 } : u))
    );
  };

  function diasDesde(data) {
    if (!data) return 0;
    const diff = Date.now() - new Date(data).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  function textoCadastro(data) {
    if (!data) return "cadastrado hoje";

    const hoje = new Date();
    const criado = new Date(data);

    hoje.setHours(0, 0, 0, 0);
    criado.setHours(0, 0, 0, 0);

    const dias = Math.floor(
      (hoje.getTime() - criado.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (dias <= 0) return "cadastrado hoje";
    if (dias === 1) return "cadastrado ontem";
    return `cadastrado há ${dias} dias`;
  }

  function getAcaoSugerida(user) {
    if (user.is_paid) return null;

    const etapaNum =
      Number(
        normalizarEtapaVendasLegada(user.etapa_vendas) ??
          user.etapa_vendas ??
          1
      ) || 1;

    const regras = {
      1: {
        key: "contato",
        titulo: "FAZER CONTATO",
        cor: "#2196F3",
      },
      2: {
        key: "negociacao",
        titulo: "OFERECER DESCONTO R$199",
        cor: "#4CAF50",
      },
      3: {
        key: "nova_condicao",
        titulo: "OFERECER DESCONTO R$179",
        cor: "#FF9800",
      },
      4: {
        key: "oferta_final",
        titulo: "OFERTA FINAL R$149",
        cor: "#F44336",
      },
    };

    const regra = regras[etapaNum];
    if (!regra) {
      return {
        titulo: "LEAD FRIO",
        colorHex: "#9E9E9E",
      };
    }

    return {
      key: regra.key,
      titulo: regra.titulo,
      colorHex: regra.cor,
    };
  }

  function textoDiasDesde(data) {
    if (!data) return "criado hoje";

    const hoje = new Date();
    const criado = new Date(data);

    // zera hora/minuto/segundo
    hoje.setHours(0, 0, 0, 0);
    criado.setHours(0, 0, 0, 0);

    const dias = Math.round(
      (hoje.getTime() - criado.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (dias <= 0) return "criado hoje";
    if (dias === 1) return "criado ontem";
    return `criado há ${dias} dias`;
  }

  const enrichedUsers = users.map((u) => ({
    ...u,
    dias_desde_cadastro: diasDesde(u.created_at),
    texto_dias: textoDiasDesde(u.created_at),
    texto_cadastro: textoCadastro(u.created_at),
    acao_sugerida: getAcaoSugerida(u),
  }));

  const handleUpdateUser = (updatedUser) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === updatedUser.id ? updatedUser : u))
    );
  };

  return {
    users: enrichedUsers,
    filteredUsers: enrichedUsers,
    currentPage,
    hasMore,
    totalUsers,
    paidCount,
    unpaidCount,
    testCount,
    loading,
    openDialog,
    setOpenDialog,
    userToDelete,
    setUserToDelete,
    searchQuery,
    setSearchQuery,
    observerRef,
    handleRefresh,
    handleDeleteUser,
    handleUpdateDiscount,
    handleUpdateTags,
    handleRenewalNotified,
    handleUpdateUser,
    externalFilter,
    orderFilter,
    setOrderFilter,
  };
};
